import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { getFeedbacks } from '../../services/apis';

const defaultStyles = {
  flexGrow: 1,
  p: 3,
  m: 3,
  mt: 8,
  ml: 0,
  mr: 0,
  backgroundColor: '#fff',
  overflow: 'auto',
  borderRadius: 2,
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  bgcolor: 'background.paper',
  border: '1px solid #ccc',
  borderRadius: '8px',
  boxShadow: 24,
  p: 4,
};

const headingStyles = { fontSize: '18px', fontWeight: 'bold' };

const feedbackItems = ['all', 'issue', 'new_idea', 'feedback'];

const Feedbacks = () => {
  const theme = useTheme();

  const feedbacksData = useRef([]);

  const [selected, setSelected] = useState(feedbackItems[0]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const handleFeedbackSelection = (item) => {
    setSelected(item);
    if (item === 'all') {
      setRows(feedbacksData.current);
    } else {
      const filteredRows = feedbacksData.current.filter(
        (feedback) => feedback.feedback_type === item
      );
      setRows(filteredRows);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleView = (row) => {
    setSelectedDetails(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDetails(null);
  };

  const handleGetFeedbacks = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const {
        data: { feedbacks },
      } = await getFeedbacks();
      feedbacksData.current = feedbacks;

      if (feedbacks && feedbacks.length) {
        const columnsData = [...Object.keys(feedbacks[0]), 'action'].map((key) => ({
          id: key,
          label: key.replaceAll('_', ' '),
          minWidth: 170,
          align: 'center',
          format: (value) => value.toLocaleString('en-US'),
        }));
        setColumns(columnsData);

        const rowsData = feedbacks.map((feedback) => ({
          feedback_type: feedback.feedback_type,
          app_name: feedback.app_name,
          feedback_description: feedback.feedback_description,
          user_display_app_name: feedback.user_display_app_name,
          user_name: feedback.user_name,
          created_at: feedback.created_at,
        }));
        setRows(rowsData);
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetFeedbacks();
  }, []);

  return (
    <Box>
      <Box component="main" sx={defaultStyles}>
        <Box display="flex" alignItems={'center'} gap={3}>
          <Typography variant="h6" sx={headingStyles}>
            Feedback Type
          </Typography>
          {feedbackItems.map((item) => (
            <Button
              key={item}
              variant={selected === item ? 'contained' : 'outlined'}
              color={selected === item ? 'active' : 'inherit'}
              onClick={() => handleFeedbackSelection(item)}
              sx={{
                textTransform: 'capitalize',
                fontSize: '15px',
                fontWeight: 'bold',
                borderColor: selected === item ? theme.palette.active.main : theme.border.color,
                color:
                  selected === item ? theme.palette.primary.default : theme.palette.active.main,
              }}
            >
              {item.replace('_', ' ')}
            </Button>
          ))}
        </Box>
      </Box>
      <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="modal-title">
        <Box sx={modalStyle} display={'flex'} flexDirection={'column'}>
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            Details
          </Typography>
          {selectedDetails && (
            <>
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ whiteSpace: 'normal' }}>
                    <strong>App Name:</strong> {selectedDetails.app_name}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'normal' }}>
                    <strong>Feedback:</strong> {selectedDetails.feedback_description}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ whiteSpace: 'normal' }}>
                    <strong>User Name:</strong> {selectedDetails.user_name}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'normal' }}>
                    <strong>Status:</strong> {selectedDetails.feedback_type}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button variant="outlined" color="primary" onClick={handleCloseModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <Box component="main" sx={{ ...defaultStyles, mt: 0 }}>
        <Typography variant="h6" sx={{ ...headingStyles, mb: 2 }}>
          User Comments
        </Typography>
        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" height={440}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={440}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                        sx={{ fontWeight: 'bold', fontSize: '15px', textTransform: 'capitalize' }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => {
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                          {columns.map((column) => {
                            const value = row[column.id];
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{ fontSize: '15px' }}
                              >
                                {column.id === 'action' ? (
                                  <Button
                                    sx={{ backgroundColor: theme.palette.active.main }}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleView(row)}
                                  >
                                    View
                                  </Button>
                                ) : column.format && typeof value === 'number' ? (
                                  column.format(value)
                                ) : (
                                  value
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default Feedbacks;
